import {
  DownloadableCheckoutData,
  DownloadableCheckoutDialog,
  EResourceCheckoutResponse,
} from "./DownloadableCheckoutDialog";

import React, { createContext, useContext } from "react";
import { Patron } from "@tlc/ls2pac/dto/Patron";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { useDialogWithData } from "@tlc/ui/dialog/useDialog";
import { DownloadableCheckoutSuccessDialog } from "./DownloadableCheckoutSuccessDialog";
import { AutoSyncSuccessDialog } from "./AutoSyncSuccessDialog";
import {
  OverdriveIFrameDialog,
  OverdriveIFrameDialogData,
} from "./OverdriveIFrameDialog";
import {
  DownloadableHoldData,
  DownloadableHoldDialog,
} from "./DownloadableHoldDialog";

export function getDownloadURL(
  itemId:
    | EResourceCheckoutResponse["itemId"]
    | HostSystemItemView["eresourceId"],
  service:
    | EResourceCheckoutResponse["service"]
    | HostSystemItemView["eresourceService"],
  patronId: Patron["patronId"],
) {
  const params = new URLSearchParams();
  params.set("itemId", itemId);
  params.set("service", service);
  params.set("patronId", patronId);
  return `eResource/redirect?${params.toString()}`;
}

type DownloadableDialogsContextValue = {
  openOverdriveIFrameDialog: (data: OverdriveIFrameDialogData) => void;
  openCheckoutDialog: (data: DownloadableCheckoutData) => void;
  openPlaceHoldDialog: (data: DownloadableHoldData) => void;
};

export const DownloadableDialogsContext =
  createContext<DownloadableDialogsContextValue | null>(null);
export const DownloadableDialogsProvider = ({
  children,
}: React.PropsWithChildren) => {
  const {
    dialogProps: overdriveDialogProps,
    openDialog: openOverdriveIFrameDialog,
  } = useDialogWithData<{
    src: string;
  }>();

  const {
    openDialog: openCheckoutDialog,
    dialogProps: checkoutDialogProps,
    closeDialog: closeCheckoutDialog,
  } = useDialogWithData<DownloadableCheckoutData>();

  const { openDialog: openPlaceHoldDialog, dialogProps: placeHoldDialogProps } =
    useDialogWithData<DownloadableHoldData>();

  // internal dialogs
  const { openDialog: openSuccessDialog, dialogProps: successDialogProps } =
    useDialogWithData<EResourceCheckoutResponse>();
  const { openDialog: openAutosyncDialog, dialogProps: autosyncDialogProps } =
    useDialogWithData<EResourceCheckoutResponse>();

  const value = React.useMemo(
    () => ({
      openOverdriveIFrameDialog,
      openCheckoutDialog,
      openPlaceHoldDialog,
    }),
    [openOverdriveIFrameDialog, openCheckoutDialog, openPlaceHoldDialog],
  );

  return (
    <DownloadableDialogsContext.Provider value={value}>
      {overdriveDialogProps.open && (
        <OverdriveIFrameDialog {...overdriveDialogProps} />
      )}
      {successDialogProps.open && (
        <DownloadableCheckoutSuccessDialog {...successDialogProps} />
      )}
      {autosyncDialogProps.open && (
        <AutoSyncSuccessDialog {...autosyncDialogProps} />
      )}
      {checkoutDialogProps.open && (
        <DownloadableCheckoutDialog
          {...checkoutDialogProps}
          onSuccess={(response) => {
            if (response.autoSync) {
              openAutosyncDialog(response);
            } else {
              openSuccessDialog(response);
            }
            closeCheckoutDialog();
          }}
        />
      )}
      {placeHoldDialogProps.open && (
        <DownloadableHoldDialog {...placeHoldDialogProps} />
      )}
      {children}
    </DownloadableDialogsContext.Provider>
  );
};

export function useDownloadableDialogs() {
  const context = useContext(DownloadableDialogsContext);

  if (!context) {
    throw new Error(
      "useDownloadableDialogs must be used within an DownloadableDialogsProvider",
    );
  }

  return context;
}
